import { Component, Inject, Renderer2, DOCUMENT } from '@angular/core';
import { RouterOutlet, ActivatedRoute } from '@angular/router';
import { MatIconRegistry } from '@angular/material/icon';
import { filter } from 'rxjs/operators';
import { Platform } from '@angular/cdk/platform';
import { SystemPreferencesService } from './main/shared/services/system-preferences.service';
import { SidenavService } from './layout/sidenav/sidenav.service';
import { ThemeService } from '../@fury/services/theme.service';
import { SplashScreenService } from '../@fury/services/splash-screen.service';
import { FeatureFlagService } from './main/shared/services/feature-flag.service';
import { SchedulerThemeService } from './main/shared/services/scheduler-theme.service';
import { UserPreferencesService } from './main/shared/services/user-preferences.service';
import { AppThemeService } from './main/shared/services/app-theme.service';

@Component({
  selector: 'fury-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet],
})
export class AppComponent {
  constructor(
    private sidenavService: SidenavService,
    private iconRegistry: MatIconRegistry,
    private renderer: Renderer2,
    private themeService: ThemeService,
    @Inject(DOCUMENT) private document: Document,
    private platform: Platform,
    private route: ActivatedRoute,
    private splashScreenService: SplashScreenService,
    // Eagerly instantiate so it fetches feature flags from its constructor.
    private featureFlagService: FeatureFlagService,
    private systemPreferencesService: SystemPreferencesService,
    // Eagerly instantiate so it subscribes to system prefs + user session and
    // applies the effective language/units to the running app.
    private userPreferencesService: UserPreferencesService,
    // Eagerly instantiate so the saved scheduler color direction is on <html>
    // before any scheduler, timeline or dashboard surface paints.
    private schedulerThemeService: SchedulerThemeService,
    // Eagerly instantiate so the saved app theme (classic or Steel) is on <html> before first paint.
    private appThemeService: AppThemeService,
  ) {
    this.route.queryParamMap
      .pipe(filter((queryParamMap) => queryParamMap.has('style')))
      .subscribe((queryParamMap) => this.themeService.setStyle(queryParamMap.get('style')));

    this.iconRegistry.setDefaultFontSetClass('material-icons-outlined');
    this.iconRegistry.registerFontClassAlias('icomoon', 'icon');
    this.themeService.theme$.subscribe((theme) => {
      if (theme[0]) {
        this.renderer.removeClass(this.document.body, theme[0]);
      }

      this.renderer.addClass(this.document.body, theme[1]);
    });

    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }

    this.sidenavService.addItems([
      {
        name: 'dashboards',
        icon: 'mdi-microsoft',
        position: 10,
        subItems: [
          {
            name: 'machines',
            routeOrFunction: '/dashboards/machines',
            position: 10,
          },
        ],
      },
      {
        name: 'schedule',
        icon: 'mdi-calendar',
        position: 20,
        subItems: [
          {
            name: 'scheduler',
            routeOrFunction: '/schedule/jobs',
            position: 10,
          },
          {
            name: 'timeline',
            routeOrFunction: '/schedule/timeline',
            position: 10,
            // claims: 'pro.machine.schedule.timeline',
          },
          {
            name: 'plannedDowntime',
            routeOrFunction: '/schedule/downtime',
            position: 10,
            // claims: 'pro.machine.schedule.scheduler pro.machine.schedule.timeline',
          },
          {
            name: 'trucks',
            routeOrFunction: '/schedule/trucks',
            position: 10,
            claims: 'pro.machine.schedule.truck',
          },
        ],
      },
      {
        name: 'orders',
        routeOrFunction: '/orders',
        icon: 'mdi-receipt',
        navIcon: 'assets/images/steel/nav/orders.svg',
        position: 30,
        claims: 'pro.orders',
      },
      {
        name: 'punchPatterns',
        icon: 'mdi-stamper',
        position: 40,
        subItems: [
          {
            name: 'patterns',
            routeOrFunction: '/punching/patterns',
            icon: 'mdi-lock-pattern',
          },
          {
            name: 'shapes',
            routeOrFunction: '/punching/shapes',
            icon: 'mdi-shape',
          },
          {
            name: 'solutions',
            routeOrFunction: '/punching/solutions',
            icon: 'mdi-picture-in-picture-bottom-right',
          },
        ],
      },
      {
        name: 'inventory',
        icon: 'mdi-clipboard-check',
        navIcon: 'assets/images/steel/nav/inventory.svg',
        position: 50,
        claims: 'pro.inventory',
        subItems: [
          {
            name: 'materials',
            routeOrFunction: '/inventory/coil-types',
            position: 10,
          },
          {
            name: 'coils',
            routeOrFunction: '/inventory/coils',
            position: 20,
          },
        ],
      },
      {
        name: 'warehouse',
        icon: 'mdi-forklift',
        position: 60,
        claims: 'pro.machine.warehouse',
        subItems: [
          {
            name: 'tasks',
            routeOrFunction: '/warehouse/tasks',
            position: 10,
          },
          {
            name: 'users',
            routeOrFunction: '/warehouse/users',
            position: 20,
          },
          {
            name: 'appSettings',
            routeOrFunction: '/warehouse/app-settings',
            position: 30,
          },
          {
            name: 'launchApp',
            routeOrFunction: '/warehouse',
            href: '/app/warehouse/',
            target: '_blank',
            icon: 'mdi-launch',
            position: 40,
          },
        ],
      },
      {
        name: 'reports',
        icon: 'mdi-chart-bar',
        position: 70,
        subItems: [
          {
            name: 'productionSummary',
            routeOrFunction: '/report/production-summary',
            position: 10,
          },
          {
            name: 'goodProductionExplorer',
            routeOrFunction: '/production-explorer/good-production',
            position: 30,
          },
          {
            name: 'speedExplorer',
            routeOrFunction: '/production-explorer/speed',
            position: 35,
            badge: '🧪',
          },
          {
            name: 'delaySummary',
            routeOrFunction: '/report/delay-summary',
            position: 40,
          },
          {
            name: 'downtimeExplorer',
            routeOrFunction: '/production-explorer/downtime',
            position: 50,
          },
          {
            name: 'scrapSummary',
            routeOrFunction: '/report/scrap-summary',
            position: 60,
            claims: 'pro.machine.reports',
          },
          {
            name: 'scrapExplorer',
            routeOrFunction: '/production-explorer/scrap',
            position: 70,
          },
          {
            name: 'coilSummary',
            routeOrFunction: '/report/coil-summary',
            position: 80,
            badge: '🧪',
            // claims: 'pro.machine.reports',
          },
          {
            name: 'coilScrapBreakdown',
            routeOrFunction: '/report/coil-scrap',
            position: 90,
            badge: '🧪',
            // claims: 'pro.machine.reports',
          },

          {
            name: 'materialUsage',
            routeOrFunction: '/report/material-usage',
            position: 100,
            claims: 'pro.machine.reports',
            badge: '🧪',
          },
          {
            name: 'materialDemand',
            routeOrFunction: '/report/material-demand',
            position: 120,
            claims: 'pro.machine.reports',
            badge: '🧪',
          },
          {
            name: 'materialDeviations',
            routeOrFunction: '/report/material-deviations',
            position: 133,
            claims: 'pro.machine.reports',
            badge: '🧪',
          },
          {
            name: 'toolingCodeUsage',
            routeOrFunction: '/report/tooling-usage',
            position: 135,
            claims: 'pro.machine.reports',
            badge: '🧪',
          },
          {
            name: 'toolUsage',
            routeOrFunction: '/report/tool-usage',
            position: 136,
            claims: 'pro.machine.reports',
            badge: '🧪',
          },
          {
            name: 'orderSummary',
            routeOrFunction: '/report/order-summary',
            position: 138,
            claims: 'pro.machine.reports',
          },
          {
            name: 'orderSequence',
            routeOrFunction: '/report/order-sequence',
            position: 140,
            claims: 'pro.machine.reports',
          },
          {
            name: 'qualityAudit',
            routeOrFunction: '/report/quality-audit',
            position: 150,
            claims: 'pro.machine.reports',
            badge: '🧪',
          },
          {
            name: 'xlMachineConfiguration',
            routeOrFunction: '/report/xl-machine-configuration',
            position: 155,
            claims: 'pro.machine.reports',
          },
          {
            name: 'productionEvents',
            routeOrFunction: '/report/production-events',
            position: 158,
            claims: 'pro.machine.reports',
          },
          {
            name: 'pathfinderProducedGoods',
            routeOrFunction: '/report/pathfinder-produced-goods',
            position: 160,
            claims: 'pro.machine.reports',
          },
          {
            name: 'pathfinderOperationsExplorer',
            routeOrFunction: '/production-explorer/pathfinder-operations',
            position: 170,
            claims: 'pro.machine.reports.explorers',
          },
          {
            name: 'pathfinderGoodPartsExplorer',
            routeOrFunction: '/production-explorer/pathfinder-goodparts',
            position: 180,
            claims: 'pro.machine.reports.explorers',
          },
          {
            name: 'deviceProductionEvents',
            routeOrFunction: '/report/device-production-events',
            position: 190,
            claims: 'pro.machine.reports',
          },
          {
            name: 'subscriptions',
            routeOrFunction: '/report/subscriptions',
            position: 200,
            badge: '🧪',
            claims: 'pro.report-subscriptions',
          },
        ],
      },
      {
        name: 'machines',
        icon: 'mdi-factory',
        position: 80,
        subItems: [
          {
            name: 'XL200',
            routeOrFunction: '/machines/xl200',
            position: 10,
          },
          {
            name: 'Pathfinder',
            routeOrFunction: '/machines/pathfinder',
            position: 20,
            claims: 'pro.machine.dashboard',
          },
          {
            name: 'devices',
            routeOrFunction: '/machines/devices',
            position: 30,
            claims: 'pro.machine-buddy',
          },
        ],
      },
      {
        name: 'tooling',
        routeOrFunction: '/tooling',
        icon: 'mdi-hammer-wrench',
        position: 90,
      },
      {
        name: 'settings',
        icon: 'mdi-cog',
        position: 100,
        subItems: [
          {
            name: 'systemPreferences',
            routeOrFunction: '/settings/system-preferences',
            position: 10,
          },
          {
            name: 'metricConfiguration',
            routeOrFunction: '/settings/metric-config',
            position: 20,
            claims: 'pro.machine.licensed',
          },
          {
            name: 'performanceStandards',
            routeOrFunction: '/settings/performancestandards',
            position: 30,
          },
          {
            name: 'shiftTimes',
            routeOrFunction: '/settings/shift-times',
            position: 35,
          },
          {
            name: 'wallboard',
            routeOrFunction: '/settings/wallboard',
            position: 40,
          },
          {
            name: 'bundling',
            routeOrFunction: '/settings/bundling',
            position: 50,
            claims: 'pro.machine.licensed',
          },

          {
            name: 'proOnline',
            routeOrFunction: '/settings/pro-online',
            position: 55,
            claims: 'pro.machine.online',
          },
          {
            name: 'licensing',
            routeOrFunction: '/settings/licensing',
            position: 60,
          },
          {
            name: 'lossCodes',
            routeOrFunction: '/settings/losscode',
            position: 70,
          },
          {
            name: 'Quality Audits',
            routeOrFunction: '/settings/quality-audits',
            position: 75,
          },
          {
            name: 'Query Lists',
            routeOrFunction: '/settings/query-lists',
            position: 76,
          },
          {
            name: 'printing',
            routeOrFunction: '/settings/printing',
            position: 80,
            claims: 'pro.printing',
          },
          {
            name: 'integration',
            routeOrFunction: '/settings/integration',
            position: 90,
            claims: 'pro.integration',
          },
          {
            name: 'systemHealth',
            routeOrFunction: '/settings/status',
            position: 100,
          },
          {
            name: 'alertSubscriptionsTitle',
            routeOrFunction: '/settings/alert-subscriptions',
            position: 115,
            claims: 'pro.alert-subscriptions',
          },
          {
            name: 'users',
            routeOrFunction: '/settings/users',
            position: 120,
          },
        ],
      },
    ]);
  }
}
